import { useState, useRef, useEffect, useCallback } from "react";
import axios from "../axios";
import * as url from "../utils/url";
import { TRANSLATE_REQUEST_TIMEOUT_MS } from "../utils/translateResponsesCore";
import { normalizeSingleLine } from "../utils/normalizeText";

// A very large Trivvy may need a few asks before every string has its English.
const MAX_FOLLOW_UPS = 3;
const FOLLOW_UP_WAIT_MS = 3000;

const EMPTY_CONTENT = { questions: {}, name: null, messagingSubject: null };

// Options are matched on their plain text, the way the cells show a selected answer.
const optionKey = (text) => normalizeSingleLine(text);

const toContent = (data) => {
  const content = { questions: {}, name: null, messagingSubject: null };
  if (!data || typeof data !== "object") {
    return content;
  }
  if (typeof data.name === "string" && data.name.length > 0) {
    content.name = data.name;
  }
  if (typeof data.messagingSubject === "string" && data.messagingSubject.length > 0) {
    content.messagingSubject = data.messagingSubject;
  }
  (Array.isArray(data.questions) ? data.questions : []).forEach((question) => {
    if (!question || !question.questionId) {
      return;
    }
    const options = {};
    (Array.isArray(question.options) ? question.options : []).forEach((option) => {
      if (option && typeof option.text === "string" && typeof option.englishText === "string") {
        options[optionKey(option.text)] = option.englishText;
      }
    });
    content.questions[String(question.questionId)] = {
      text: typeof question.text === "string" && question.text.length > 0 ? question.text : null,
      options: options,
    };
  });
  return content;
};

// Shared Results "Translate" copy (By User + By Question): the Trivvy's own text in English. Nothing
// is fetched until the admin first picks English, so a page that stays on Original costs nothing;
// `active` (English on) decides whether the getters return it. A failed fetch is tried again the
// next time English is picked.
const useTranslatedContent = ({ triviaId, active }) => {
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [needsRetry, setNeedsRetry] = useState(false);
  const runRef = useRef(0);
  const timerRef = useRef(null);

  const stop = useCallback(() => {
    runRef.current += 1;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const load = useCallback(() => {
    stop();
    if (!triviaId) {
      return;
    }
    const runId = runRef.current;
    const ask = (followUps) => {
      axios
        .get(url.getContentTranslations(triviaId), { timeout: TRANSLATE_REQUEST_TIMEOUT_MS })
        .then((res) => {
          if (runId !== runRef.current) {
            return;
          }
          const data = res && res.data ? res.data.data : null;
          setContent(toContent(data));
          if (data && data.complete === false) {
            if (followUps < MAX_FOLLOW_UPS) {
              timerRef.current = setTimeout(() => ask(followUps + 1), FOLLOW_UP_WAIT_MS);
              return;
            }
            setNeedsRetry(true);
            return;
          }
          setNeedsRetry(false);
        })
        .catch(() => {
          // The original copy stays on screen; picking English asks again.
          if (runId === runRef.current) {
            setNeedsRetry(true);
          }
        });
    };
    setNeedsRetry(false);
    ask(0);
  }, [triviaId, stop]);

  // A different Trivvy starts clean.
  const requestedRef = useRef(false);
  useEffect(() => {
    setContent(EMPTY_CONTENT);
    setNeedsRetry(false);
    requestedRef.current = false;
    return stop;
  }, [triviaId, stop]);

  // The first switch to English asks; only a later switch asks again, so a lasting failure never loops.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    const turnedOn = active && !wasActiveRef.current;
    wasActiveRef.current = active;
    if (!active || !triviaId) {
      return;
    }
    if (!requestedRef.current || (turnedOn && needsRetry)) {
      requestedRef.current = true;
      load();
    }
  }, [active, triviaId, needsRetry, load]);

  const getQuestionText = useCallback(
    (questionId, original) => {
      const question = active ? content.questions[String(questionId)] : null;
      return question && question.text ? question.text : original;
    },
    [active, content]
  );

  const getOptionText = useCallback(
    (questionId, original) => {
      const question = active ? content.questions[String(questionId)] : null;
      if (!question || typeof original !== "string") {
        return original;
      }
      const english = question.options[optionKey(original)];
      return english ? english : original;
    },
    [active, content]
  );

  // field: "name" or "messagingSubject".
  const getMessageText = useCallback(
    (field, original) => {
      const english = active ? content[field] : null;
      return english ? english : original;
    },
    [active, content]
  );

  return {
    getQuestionText: getQuestionText,
    getOptionText: getOptionText,
    getMessageText: getMessageText,
  };
};

export default useTranslatedContent;
